/**
 * Carrier-eligibility predicate — composes the 3 axes that gate live-rate.
 *
 * Single decision point for "can this listing use this carrier's live-rate
 * shipping?". Consumed at three points per ADR §"Carrier eligibility gate":
 *  1. Listing create/edit UI — disable `shipping_mode='live_rate'` toggle.
 *  2. Server validation on listing create/edit — reject 400.
 *  3. `lib/payments/resolve-checkout-intent.ts` — defensive fallback when
 *     a mismatch row somehow exists (should be impossible post-trigger).
 *
 * The 3 axes (in order):
 *   1. capability — does Shippo rate from this origin? Read from
 *      `SHIPPO_ORIGIN_SUPPORT` (probe-verified, US/GB/DE/FR).
 *   2. access — is this country GA, or beta for this seller, or off? Read
 *      from `COUNTRY_ACCESS` × per-seller flag (the beta-flag lookup is
 *      server-only; see split-file note below).
 *   3. currency-match — Buffon-class guard (USD listing, ES origin →
 *      reject; native_currency(ES)=EUR ≠ USD).
 *
 * **Client/server split (server-only chain):** the async beta-flag lookup
 * (`hasBetaFeature`) imports `lib/supabaseAdmin.ts`, a `server-only`
 * module. Importing the async wrapper from this file would drag the
 * server-only chain into any client component using the sync predicate
 * (`EditShippingTab`, `shipping-step.tsx`). So the async version lives in
 * `./carrier-eligibility-server.ts` — client-safe consumers import from
 * here; server gates import from there.
 *
 * `isCarrierAvailable(listing, carrierId, opts?)` — SYNC. Checks
 * capability + currency. Access defaults to "GA only" unless the caller
 * passes a pre-resolved `hasBetaAccess` boolean. Use for UI render-time
 * and fitness probes.
 *
 * For the async (server moment-of-truth) variant see
 * `isCarrierAvailableForSeller` in `./carrier-eligibility-server.ts`.
 *
 * Pure function — no DB hit.
 *
 * @see packages/contract/src/money.ts — CarrierEligibility shape
 * @see lib/shipping/origin-support.ts — capability map (axis 1)
 * @see lib/shipping/country-access.ts — access map (axis 2)
 * @see lib/shipping/native-currency.ts — currency map (axis 3)
 * @see lib/shipping/carrier-eligibility-server.ts — async server wrapper
 * @see .tondocs/payments/ADR_CURRENCY_PIN.md §"Carrier eligibility gate"
 * @see .tondocs/shippo/CARRIER_ELIGIBILITY_HANDOFF_2026_06_25.md — design rationale
 */

import type {
  CarrierEligibility,
  CarrierId,
  Iso4217,
} from "@kraahl/contract";
import { CARRIER_REGISTRY } from "./carrier-registry";
import { getCountryAccess } from "./country-access";
import { nativeCurrency } from "./native-currency";
import { SHIPPO_ORIGIN_SUPPORT } from "./origin-support";

export interface EligibilityListingShape {
  currency: Iso4217 | string;
  shipFromCountry: string | null | undefined;
}

export interface EligibilityOptions {
  /**
   * Pre-resolved answer to "is this seller enrolled in the live-rate beta"
   * — relevant only when the listing's origin is in a `beta` country.
   *
   * Server callers should use the async `isCarrierAvailableForSeller` from
   * `./carrier-eligibility-server.ts` instead — it consults `beta_features`
   * as the SoT. UI render-time callers pass a value pre-resolved via the
   * `useShippoBetaAccess` hook. Omitted = treat as `false` (strict; beta
   * countries reject without an explicit grant).
   */
  hasBetaAccess?: boolean;
}

/**
 * Sync eligibility check — capability + access (with optional pre-resolved
 * beta flag) + currency-match.
 *
 * Use for UI render-time. For server gates use the async
 * `isCarrierAvailableForSeller` in `./carrier-eligibility-server.ts`.
 */
export function isCarrierAvailable(
  listing: EligibilityListingShape,
  carrierId: CarrierId,
  opts: EligibilityOptions = {},
): CarrierEligibility {
  const config = CARRIER_REGISTRY[carrierId];
  if (!config) {
    return { ok: false, reason: "CARRIER_NOT_CONFIGURED" };
  }

  const country = listing.shipFromCountry;
  const upperCountry = country ? country.toUpperCase() : null;

  // Axis 1 — capability. Probe-verified origins live in SHIPPO_ORIGIN_SUPPORT;
  // anything else means Shippo returns 0 rates for that origin on our account.
  const capability = upperCountry ? SHIPPO_ORIGIN_SUPPORT[upperCountry] : null;
  if (!capability) {
    return { ok: false, reason: "UNSUPPORTED_ORIGIN_COUNTRY" };
  }

  // Axis 2 — access. `ga` = anyone; `beta` = only with the per-seller flag;
  // `off` = no one (capability may exist but product hasn't opened it).
  const access = getCountryAccess(upperCountry);
  if (access === "off") {
    return { ok: false, reason: "UNSUPPORTED_ORIGIN_COUNTRY" };
  }
  if (access === "beta" && !opts.hasBetaAccess) {
    return { ok: false, reason: "UNSUPPORTED_ORIGIN_COUNTRY" };
  }

  // Axis 3 — currency-match. Buffon-class sellers (USD store from ES) fall
  // back to legacy flat-rate instead of being silently taxed by PayPal FX.
  const nativeForOrigin = nativeCurrency(upperCountry as string);
  if (!nativeForOrigin || nativeForOrigin !== listing.currency) {
    return { ok: false, reason: "CURRENCY_MISMATCH" };
  }

  return { ok: true, carrier: carrierId };
}
